/**
 * Slide Viewer
 * Main presentation engine with animated transitions, navigation, and TOC
 */

import React, { useState, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import type { DeckEdition } from '@components/press/pitch-deck/pitch-deck.types';
import { useSlideNavigation } from '@components/press/pitch-deck/hooks/use-slide-navigation';
import { DeckOpeningContext } from '@components/press/pitch-deck/hooks/use-deck-opening';
import { TableOfContents } from '@components/press/pitch-deck/table-of-contents';
import {
  ViewerContainer,
  Ground,
  SlideWrapper,
  MotionSlide,
  ProgressBar,
  ProgressFill,
  NavButton,
  SlideCounter,
  MenuButton,
  DeckTitle,
  Copyright,
  Watermark,
} from '@components/press/pitch-deck/slide-viewer.styles';

interface SlideViewerProps {
  edition: DeckEdition;
  watermark?: string;
}

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? '100%' : '-100%',
    opacity: 0,
  }),
};

const slideTransition = {
  x: { type: 'tween' as const, duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] },
  opacity: { duration: 0.3 },
};

/* The ground turns over while the outgoing slide leaves, so the incoming
   one arrives on what it stands on. */
const groundTransition = { duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] };

export const SlideViewer: React.FC<SlideViewerProps> = ({ edition, watermark }) => {
  const { slides } = edition;
  const [tocOpen, setTocOpen] = useState(false);

  const {
    currentSlide,
    direction,
    goToSlide,
    goNext,
    goPrevious,
    isFirst,
    isLast,
  } = useSlideNavigation({
    totalSlides: slides.length,
    enabled: !tocOpen,
  });

  const progress = useMemo(
    () => ((currentSlide + 1) / slides.length) * 100,
    [currentSlide, slides.length]
  );

  const CurrentSlideComponent = slides[currentSlide].component;
  const isMainSlide = slides[currentSlide].category === 'main';
  const mainSlideCount = slides.filter(s => s.category === 'main').length;
  const mainSlideNumber = isMainSlide ? currentSlide + 1 : null;
  const onWater = slides[currentSlide].backdrop === 'water';
  // No navigation yet: the slide on screen is the one the deck opened on.
  const opening = direction === 0;

  return (
    <ViewerContainer>
      <Ground
        aria-hidden="true"
        initial={false}
        animate={{ opacity: onWater ? 0 : 1 }}
        transition={groundTransition}
        style={edition.ground ? { background: edition.ground } : undefined}
      />

      <ProgressBar>
        <ProgressFill $progress={progress} />
      </ProgressBar>

      <DeckTitle>{edition.label}</DeckTitle>
      <Copyright>LAT LONG LABS &copy; 2026</Copyright>

      <MenuButton onClick={() => setTocOpen(true)} aria-label="Table of contents">
        &#9776;
      </MenuButton>

      {/* The opening slide skips its slide-in, set on the slide itself
          rather than as AnimatePresence's `initial={false}`: that would
          also block every entrance animation inside the slide, so a slide
          could never give itself an arrival (see `use-deck-opening`). */}
      <SlideWrapper>
        <DeckOpeningContext.Provider value={opening}>
          <AnimatePresence custom={direction} mode="wait">
            <MotionSlide
              key={currentSlide}
              custom={direction}
              variants={slideVariants}
              initial={opening ? false : 'enter'}
              animate="center"
              exit="exit"
              transition={slideTransition}
            >
              <CurrentSlideComponent />
            </MotionSlide>
          </AnimatePresence>
        </DeckOpeningContext.Provider>
      </SlideWrapper>

      {watermark && <Watermark data-text={watermark} />}

      <NavButton
        $side="left"
        onClick={goPrevious}
        disabled={isFirst}
        aria-label="Previous slide"
      >
        &#8249;
      </NavButton>

      <NavButton
        $side="right"
        onClick={goNext}
        disabled={isLast}
        aria-label="Next slide"
      >
        &#8250;
      </NavButton>

      {isMainSlide && (
        <SlideCounter>
          {mainSlideNumber} / {mainSlideCount}
        </SlideCounter>
      )}

      <TableOfContents
        title={edition.tocTitle}
        slides={slides}
        downloads={edition.downloads}
        currentSlide={currentSlide}
        onNavigate={goToSlide}
        onClose={() => setTocOpen(false)}
        isOpen={tocOpen}
      />
    </ViewerContainer>
  );
};
